/**
 * A Toggle component, sometimes referred to as a 'pill'. Can be in either the on or off position.
 */

import classNames from "classnames";
import { throttle } from "lodash";
import { observable, runInAction } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import ReactTooltip from "react-tooltip";

export enum ToggleTheme {
  Light = "light",
  Dark = "dark",
}

export interface IToggleProps {
  value: boolean;
  onChange?: (isActive: boolean) => void;
  label?: string;
  disabled?: boolean;
  getDisabledMessage?: () => Promise<string>;
  theme?: ToggleTheme;
}

@observer
export default class Toggle extends React.Component<IToggleProps, {}> {
  public static defaultProps = {
    theme: ToggleTheme.Light,
  };

  private changeState: () => void;

  @observable.ref
  private disabledMessage: string = "placeholder";

  constructor(props: IToggleProps) {
    super(props);

    this.changeState = throttle(this.toggleValue, 200, { leading: true, trailing: false });

    if (!props.getDisabledMessage) {
      this.disabledMessage = "";
    } else {
      this.disabledMessage = "";
      // We don't want to block the constructor while fetching the disabled message
      // Currently ReactTooltip will only fetch the tooltip message on the mouseenter event for the element. If the user
      // already has their cursor over the Toggle element then the disabledMessage won't immediately show until
      // the next time their cursor enters the target element
      props.getDisabledMessage().then((result) => {
        runInAction(() => (this.disabledMessage = result));
      });
    }
  }

  public render() {
    return (
      <div className={`toggle-button toggle-button--${this.props.theme}`} aria-disabled={this.props.disabled}>
        <div
          className={classNames([
            "toggle-button__click-field",
            { "toggle-button__click-field--is-disabled": this.props.disabled },
          ])}
          onClick={this.changeState}
        >
          {this.renderLabel()}
          {this.renderPill()}
        </div>
        <ReactTooltip id="toggle-button-tooltip" effect="solid" place="bottom" className="tooltip--helper-theme" />
      </div>
    );
  }

  private renderLabel() {
    return (
      <label
        className={classNames(["toggle-button__label", { "toggle-button__label--disabled": this.props.disabled }])}
      >
        {this.props.label}
      </label>
    );
  }

  private renderPill() {
    return (
      <div
        data-tip={this.disabledMessage}
        data-for="toggle-button-tooltip"
        className={classNames([
          "toggle-button__mount",
          {
            "toggle-button__mount--is-toggled": this.props.value,
            "toggle-button__mount--is-disabled": this.props.disabled,
          },
        ])}
      >
        <div className="toggle-button__switch"></div>
      </div>
    );
  }

  private toggleValue = () => {
    if (this.props.disabled) {
      return;
    }

    if (this.props.onChange) {
      this.props.onChange(!this.props.value);
    }
  };
}
